import moment from 'moment';
import React from "react";

const formatDurationSeconds = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

const getModeIconClass = (mode) => {
    const modeMap = {
        'CAR': 'drive',
        'WALK': 'walk',
        'BUS': 'bus',
        'RAIL': 'rail',
        'TRAM': 'tram',
        'SUBWAY': 'subway',
        'METRO': 'metro'
    };
    
    return modeMap[mode?.toUpperCase()] || mode?.toLowerCase() || 'walk';
};

const getDestinationText = (leg, legIndex, allLegs, finalDestination) => {
    // For transit legs with stops, use the stop name
    if (leg.to?.stop?.name) {
        return leg.to.stop.name;
    }
    
    // For walking/driving legs, find the next transit leg
    const nextTransitLeg = allLegs.slice(legIndex + 1).find(nextLeg => nextLeg.trip?.gtfsId);
    
    if (nextTransitLeg?.from?.stop?.name) {
        return nextTransitLeg.from.stop.name;
    }
    
    // If no next transit leg, check if this is the very last leg
    const isLastLeg = legIndex === allLegs.length - 1;
    
    if (isLastLeg) {
        // Use final journey destination for the very last leg
        return finalDestination || leg.to?.name || 'Destination';
    } else {
        // Use the leg's own destination as specified in JP response
        return leg.to?.name || 'Destination';
    }
};

export function JourneySummary({ nextPlan, onClose, onSend, selectedMode = 'RAIL' }) {
    if (!nextPlan?.node) {
        return null;
    }

    const legWithTrip = nextPlan.node?.legs?.find(leg =>
        leg.trip?.stoptimesForDate?.[0]?.departureDelay !== undefined
    );

    const lastLegWithTrip = [...(nextPlan.node?.legs || [])].reverse().find(leg =>
        leg.trip?.stoptimesForDate?.[0]?.departureDelay !== undefined
    );

    const startTime = moment(nextPlan.node?.start);
    
    // Get origin and destination names based on mode
    let originName, destinationName, legsToShow;
    
    if (selectedMode === 'BUS') {
        // For bus mode: use transit leg boundaries for origin/destination
        const transitLegs = nextPlan.node?.legs?.filter(leg => leg.trip?.gtfsId) || [];
        
        if (transitLegs.length > 0) {
            // Use first transit leg origin and last transit leg destination
            const firstTransitLeg = transitLegs[0];
            const lastTransitLeg = transitLegs[transitLegs.length - 1];
            originName = firstTransitLeg?.from?.stop?.name || firstTransitLeg?.from?.name;
            destinationName = lastTransitLeg?.to?.stop?.name || lastTransitLeg?.to?.name;
        } else {
            // No transit legs, use journey start/end
            originName = nextPlan.node?.legs?.[0]?.from?.name || nextPlan.node?.legs?.[0]?.from?.stop?.name;
            destinationName = nextPlan.node?.legs?.[nextPlan.node?.legs?.length - 1]?.to?.name || nextPlan.node?.legs?.[nextPlan.node?.legs?.length - 1]?.to?.stop?.name;
        }
        
        // For bus mode: show all legs including walking
        legsToShow = nextPlan.node?.legs?.filter(leg => leg.mode) || [];
    } else {
        // For rail mode: keep existing behavior
        originName = legWithTrip?.from?.stop?.name;
        destinationName = lastLegWithTrip?.to?.stop?.name;
        legsToShow = nextPlan.node?.legs?.filter(leg => leg.trip?.gtfsId) || [];
    }

    // Dynamic spacing based on number of legs
    const getSpacingClass = (numLegs) => {
        if (numLegs <= 2) return 'space-y-4 sm:space-y-6';
        if (numLegs === 3) return 'space-y-3 sm:space-y-4';
        if (numLegs === 4) return 'space-y-2 sm:space-y-3';
        return 'space-y-1 sm:space-y-2'; // 5+ legs
    };

    // Dynamic padding based on number of legs
    const getPaddingClass = (numLegs) => {
        if (numLegs <= 2) return 'py-3 sm:py-4 lg:py-6';
        if (numLegs === 3) return 'py-2 sm:py-3 lg:py-4';
        if (numLegs === 4) return 'py-1 sm:py-2 lg:py-3';
        return 'py-1 sm:py-1 lg:py-2'; // 5+ legs
    };

    const numLegs = legsToShow.length;
    const spacingClass = getSpacingClass(numLegs);
    const paddingClass = getPaddingClass(numLegs);
    const shouldScroll = numLegs > 5;

    return (
        <div className="w-full h-full bg-white flex flex-col relative">
                {/* Header */}
                <div className="pt-4 sm:pt-8 px-4 sm:px-8 flex justify-between items-start mb-4 sm:mb-8">
                    <div className="flex-1 pr-2">
                        <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-black">
                            {originName} to {destinationName}
                        </h1>
                        <p className="text-sm sm:text-base lg:text-lg text-gray-600 mt-1">
                            {startTime.format('DD MMMM YYYY')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 sm:w-12 sm:h-12 bg-black text-white rounded-full transition-colors flex items-center justify-center">
                        <i className="zip-icon-int_arrow_round_left text-2xl sm:text-3xl lg:text-4xl"></i>
                    </button>
                </div>

                {/* Journey Legs */}
                <div className={`px-4 sm:px-8 ${spacingClass} ${shouldScroll ? 'overflow-y-auto max-h-96' : ''}`}>
                    {legsToShow.map((leg, index) => (
                        <div key={index} className={`flex items-center justify-between ${paddingClass}`}>
                            <div className="flex items-center gap-4 sm:gap-6 w-full">
                                <i className={`self-start text-2xl sm:text-3xl lg:text-4xl zip-icon-mode_${getModeIconClass(leg?.mode)}`}></i>
                                <div className="flex-1">
                                    <p className="text-base sm:text-lg lg:text-xl font-bold text-black mt-1">
                                        {moment(leg.from?.departure?.scheduledTime).format('HH:mm')} to {getDestinationText(leg, index, nextPlan.node?.legs || [], destinationName)}
                                    </p>
                                    <p className="text-sm sm:text-base lg:text-lg text-gray-600">
                                        {formatDurationSeconds(leg.duration)} 
                                        {leg?.agency?.name && (
                                            <span className="text-gray-600 ml-2 sm:ml-4 leading-none">
                                                {leg.agency.name}
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Send Button */}
                <div className="absolute bottom-4 sm:bottom-8 right-4 sm:right-8">
                    <button 
                        onClick={onSend}
                        className="px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-journeyAlertsYellow text-black font-coheadline-bold text-lg sm:text-xl lg:text-2xl">
                        Share
                    </button>
                </div>
        </div>
    );
} 